/**
 * Read-only viewer for finalized onboarding archive files on disk
 * ({email}/archive/{invitationId}/various_copies|forms).
 */
import React, { useCallback, useEffect, useState } from 'react';
import folderIcon from '../../../../assests/icons/folder.png';
import downloadIcon from '../../../../assests/icons/download.png';
import downloadAllIcon from '../../../../assests/icons/downloadAll.png';
import {
  buildOnboardingArchiveFileUrl,
  downloadOnboardingArchiveZip,
  fetchOnboardingArchiveContents,
  type ArchiveFileItemApi,
} from '../../../../services/onboardingFinalizeApi';
import { fetchChatFlow, type ChatFlowProfileSectionItem } from '../../../../services/chatFlowApi';
import ProfileSectionPreview from './ProfileSectionPreview';
import { downloadDocumentFile, isImageExtension, isPdfExtension, safeFileNamePart } from './utils';
import './CheckSubmissions.css';
import './DocumentPreview.css';

export interface FinalizedArchiveViewerProps {
  email: string;
  invitationId: string;
  employeeName?: string;
  /** Which roadmap section to show */
  section: 'various' | 'filling';
  /**
   * Only the newest archived period may show live 基本情報 / 家族情報
   * (older periods rely on archived form PDFs only).
   */
  showProfileSections?: boolean;
  onCountsChange?: (counts: { various: number; forms: number }) => void;
}

const FinalizedArchiveViewer: React.FC<FinalizedArchiveViewerProps> = ({
  email,
  invitationId,
  employeeName,
  section,
  showProfileSections = false,
  onCountsChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [various, setVarious] = useState<ArchiveFileItemApi[]>([]);
  const [forms, setForms] = useState<ArchiveFileItemApi[]>([]);
  const [profileSections, setProfileSections] = useState<ChatFlowProfileSectionItem[]>([]);
  const [preview, setPreview] = useState<ArchiveFileItemApi | null>(null);
  const [profilePreview, setProfilePreview] = useState<ChatFlowProfileSectionItem | null>(null);
  const [zipBusy, setZipBusy] = useState(false);

  const load = useCallback(() => {
    if (!email.trim() || !invitationId.trim()) {
      setVarious([]);
      setForms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const archivePromise = fetchOnboardingArchiveContents(email, invitationId);
    const profilePromise = showProfileSections
      ? fetchChatFlow(email, 'ja', invitationId).then((r) => r?.profileSections ?? [])
      : Promise.resolve([] as ChatFlowProfileSectionItem[]);

    Promise.all([archivePromise, profilePromise])
      .then(([contents, profiles]) => {
        setVarious(contents.variousCopies ?? []);
        setForms(contents.forms ?? []);
        setProfileSections(showProfileSections ? profiles : []);
        onCountsChange?.({
          various: (contents.variousCopies ?? []).length,
          forms: (contents.forms ?? []).length,
        });
      })
      .catch((e) => {
        setVarious([]);
        setForms([]);
        setProfileSections([]);
        setError(e instanceof Error ? e.message : 'アーカイブの読み込みに失敗しました。');
        onCountsChange?.({ various: 0, forms: 0 });
      })
      .finally(() => setLoading(false));
  }, [email, invitationId, showProfileSections, onCountsChange]);

  useEffect(() => {
    load();
  }, [load]);

  const fileUrl = (item: ArchiveFileItemApi) =>
    buildOnboardingArchiveFileUrl(email, invitationId, item.relativePath);

  const handleDownloadFile = async (item: ArchiveFileItemApi) => {
    const label = item.displayName || item.code || 'document';
    const base = safeFileNamePart(employeeName?.trim() || email);
    const extMatch = item.fileName.match(/\.[^.]+$/);
    const ext = extMatch ? extMatch[0] : '';
    const fileName = `${base}_${safeFileNamePart(label)}${ext}`;
    try {
      await downloadDocumentFile(fileUrl(item), fileName);
    } catch {
      alert(`${label} のダウンロードに失敗しました。`);
    }
  };

  const handleDownloadZip = async () => {
    setZipBusy(true);
    try {
      const name = safeFileNamePart(employeeName?.trim() || email) + '_書類一式.zip';
      await downloadOnboardingArchiveZip(email, invitationId, name);
    } catch {
      alert('ZIPのダウンロードに失敗しました。');
    } finally {
      setZipBusy(false);
    }
  };

  const renderPreview = () => {
    if (!preview) return null;
    const url = fileUrl(preview);
    const name = preview.fileName;
    const isPdf = isPdfExtension(name);
    const isImage = isImageExtension(name);
    return (
      <div className="document-preview" role="dialog" aria-modal="true">
        <div className="document-preview__backdrop" onClick={() => setPreview(null)} aria-hidden />
        <div className="document-preview__panel">
          <header className="document-preview__header">
            <h2 className="document-preview__title">{preview.displayName || preview.fileName}</h2>
            <button
              type="button"
              className="document-preview__close-btn"
              aria-label="閉じる"
              onClick={() => setPreview(null)}
            >
              ×
            </button>
          </header>
          <div className="document-preview__body">
            {isPdf && (
              <iframe title={preview.fileName} src={url} className="document-preview__iframe" />
            )}
            {isImage && !isPdf && (
              <img src={url} alt={preview.displayName} className="document-preview__img" />
            )}
            {!isPdf && !isImage && (
              <p>
                この形式はプレビューできません。
                <a href={url} target="_blank" rel="noreferrer">
                  ダウンロード
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const title = section === 'various' ? 'アップロード書類（アーカイブ）' : '記入書類（アーカイブ）';
  const rows = section === 'various' ? various : forms;
  const hasFormRows = forms.length > 0 || profileSections.length > 0;
  const emptyLabel =
    section === 'various'
      ? 'アーカイブにアップロード書類がありません'
      : 'アーカイブに記入書類がありません';
  const colSpan = 4;

  return (
    <div className="check-submissions__table-wrapper">
      <div className="check-submissions__table-header">
        <div className="check-submissions__table-title">{title}</div>
        <button
          type="button"
          className="check-submissions__download-all-btn"
          onClick={handleDownloadZip}
          disabled={zipBusy || loading}
        >
          <img
            src={downloadAllIcon}
            alt=""
            className="check-submissions__icon-img check-submissions__icon-img--download"
            aria-hidden
          />
          <span>{zipBusy ? 'ZIP作成中…' : 'すべてダウンロード'}</span>
        </button>
      </div>

      {error && (
        <p className="check-submissions__td-empty" role="alert">
          {error}
        </p>
      )}

      <table className="check-submissions__table check-submissions__table--archive">
        <thead>
          <tr>
            <th className="check-submissions__th-name">書類名</th>
            <th>状態</th>
            <th>ファイル</th>
            <th>ダウンロード</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colSpan} className="check-submissions__td-empty">
                読み込み中...
              </td>
            </tr>
          ) : section === 'various' && various.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="check-submissions__td-empty">
                {emptyLabel}
              </td>
            </tr>
          ) : section === 'filling' && !hasFormRows ? (
            <tr>
              <td colSpan={colSpan} className="check-submissions__td-empty">
                {emptyLabel}
              </td>
            </tr>
          ) : (
            <>
              {section === 'filling' &&
                profileSections.map((sectionRow) => (
                  <tr key={sectionRow.section}>
                    <td className="check-submissions__td-name">
                      {sectionRow.nameJa ??
                        sectionRow.name ??
                        (sectionRow.section === 'family_info' ? '家族情報' : '基本情報')}
                    </td>
                    <td>最新</td>
                    <td>
                      <button
                        type="button"
                        className="check-submissions__icon-btn check-submissions__icon-btn--file"
                        aria-label="プレビュー"
                        onClick={() => setProfilePreview(sectionRow)}
                      >
                        <img
                          src={folderIcon}
                          alt=""
                          className="check-submissions__icon-img check-submissions__icon-img--folder"
                          aria-hidden
                        />
                      </button>
                    </td>
                    <td>
                      <span className="check-submissions__file-dash">—</span>
                    </td>
                  </tr>
                ))}
              {rows.map((item) => (
                <tr key={item.relativePath}>
                  <td className="check-submissions__td-name">{item.displayName || item.code}</td>
                  <td>アーカイブ済</td>
                  <td>
                    <button
                      type="button"
                      className="check-submissions__icon-btn check-submissions__icon-btn--file"
                      aria-label={`${item.displayName} をプレビュー`}
                      onClick={() => setPreview(item)}
                    >
                      <img
                        src={folderIcon}
                        alt=""
                        className="check-submissions__icon-img check-submissions__icon-img--folder"
                        aria-hidden
                      />
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="check-submissions__icon-btn check-submissions__icon-btn--download"
                      aria-label={`${item.displayName} をダウンロード`}
                      onClick={() => void handleDownloadFile(item)}
                    >
                      <img
                        src={downloadIcon}
                        alt=""
                        className="check-submissions__download-icon"
                        aria-hidden
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {renderPreview()}
      {profilePreview && (
        <ProfileSectionPreview
          section={profilePreview}
          employeeEmail={email}
          invitationId={invitationId}
          readOnly
          onClose={() => setProfilePreview(null)}
          showResubmitModal={false}
          resubmitReason=""
          onResubmitReasonChange={() => {}}
          onOpenResubmitModal={() => {}}
          onCloseResubmitModal={() => {}}
          onApprove={() => {}}
          onSaveResubmit={() => {}}
        />
      )}
    </div>
  );
};

export default FinalizedArchiveViewer;
